/**
 * Formatting utility functions for the CDC Audit frontend.
 */

/**
 * Format an ISO timestamp string to locale string.
 * @param {string|Date} timestamp - ISO 8601 string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string or '-' if falsy
 *
 * @example
 * formatTimestamp('2024-06-01T10:30:00Z') // '6/1/2024, 6:30:00 PM'
 * formatTimestamp(null) // '-'
 */
export function formatTimestamp(timestamp, options) {
    if (!timestamp) return '-';
    try {
        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        return date.toLocaleString(undefined, options);
    } catch {
        return String(timestamp);
    }
}

/**
 * Format a timestamp to date only (no time).
 * @param {string|Date} timestamp
 * @returns {string}
 *
 * @example
 * formatDate('2024-06-01T10:30:00Z') // '6/1/2024'
 */
export function formatDate(timestamp) {
    return formatTimestamp(timestamp, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });
}

/**
 * Format a timestamp to time only (no date).
 * @param {string|Date} timestamp
 * @returns {string}
 *
 * @example
 * formatTime('2024-06-01T10:30:00Z') // '6:30:00 PM'
 */
export function formatTime(timestamp) {
    return formatTimestamp(timestamp, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });
}

/**
 * Format a timestamp as relative time (e.g., "2 hours ago").
 * @param {string|Date} timestamp
 * @returns {string}
 *
 * @example
 * formatRelativeTime('2024-06-01T10:30:00Z') // '2 hours ago'
 */
export function formatRelativeTime(timestamp) {
    if (!timestamp) return '-';
    try {
        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
        const now = Date.now();
        const diffMs = now - date.getTime();
        const diffSec = Math.floor(diffMs / 1000);

        if (diffSec < 60) return 'just now';
        if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
        if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`;
        if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} days ago`;
        return formatTimestamp(timestamp);
    } catch {
        return String(timestamp);
    }
}

/**
 * Format a number with locale-specific thousands separators.
 * @param {number|string} value
 * @returns {string}
 *
 * @example
 * formatNumber(1234567) // '1,234,567'
 * formatNumber(null) // '0'
 */
export function formatNumber(value) {
    if (value == null || value === '') return '0';
    const num = typeof value === 'string' ? Number(value) : value;
    if (isNaN(num)) return String(value);
    return num.toLocaleString();
}

/**
 * Format a value to string, handling null/undefined/objects.
 * @param {*} value
 * @returns {string}
 *
 * @example
 * formatValue(null) // '-'
 * formatValue({ key: 'val' }) // '{"key":"val"}'
 * formatValue('hello') // 'hello'
 */
export function formatValue(value) {
    if (value == null || value === '') return '-';
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
}

/**
 * Truncate a string to a maximum length with ellipsis.
 * @param {string} str
 * @param {number} maxLen - Maximum length (default 40)
 * @returns {string}
 *
 * @example
 * truncate('oracle:XEPDB1:tx:5.7.12345', 20) // 'oracle:XEPDB1:tx:5...'
 */
export function truncate(str, maxLen = 40) {
    if (!str) return '';
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen - 3) + '...';
}

/**
 * Format bytes to human-readable size.
 * @param {number} bytes
 * @returns {string}
 *
 * @example
 * formatBytes(1024) // '1 KB'
 * formatBytes(1048576) // '1 MB'
 */
export function formatBytes(bytes) {
    if (bytes == null || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}
